import React, { useEffect, useState } from 'react';

import PageWrapper from '../PageWrapper';
import Page from '../Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleAccordion,
  BubbleButton,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleTypeahead,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
  BubbleBadge,
  BubbleDebounce,
  BubbleTooltip,
  BubbleCheckbox,
} from '@go-client/components/BubbleUI';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import ReserveNavigation from '@go-client/components/Navigations/ReserveNavigation';
import ReserveSubNavigation from '@go-client/components/Navigations/ReserveSubNavigation';
import ReservationPath, {
  ReservationFormMode,
} from '@go-client/components/ReservationPath/ReservationPath';
import { Reservation, ReservationExportType } from '@go-shared/types/reservation';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { readQs } from '@go-shared/lib/querystring';
import {
  get as getReservation,
  toggleStatus,
  releaseSpot,
  paginateReservations,
  exportTransactionsToCsv,
} from '@go-client/lib/api/reservation';
import { BubbleBadgeProps } from '@go-client/components/BubbleUI/BubbleBadge';
import { hasOwnProperty, makeFullName, formatCentsToUSD } from '@go-shared/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  calculateReservationAnalytics,
  getEnhancedReservationStatus,
  getEnhancedStatusVariant,
  isFlattenedReservation,
  isReservationReleased,
  summarizeOccurrences,
} from '@go-shared/lib/reserve';
import { ReservationTableFields, ReservationTableItem } from './ReserveOngoing';
import {
  BubbleTableFieldBehavior,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import { SimpleUser, User } from '@go-shared/types/user';
import { getSimpleUsers } from '@go-client/lib/api/user';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { DateGroup, dateToIsoUTC, getLocaleDateTimeStringLocal } from '@go-shared/lib/time';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { getHourFormat } from '@go-shared/lib/property';
import qslib from '@go-shared/lib/querystring';
import { Alert, Anchor } from '@spark-labs/avocado-ui';
import { mapBy } from '@go-shared/lib/functional';
import { HourFormat } from '@go-shared/types/brand';
import {
  canCreateSomeReservations,
  canHostEvents,
  canManageAllReservations,
} from '@go-shared/lib/permissions';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { BubbleButtonProps } from '@go-client/components/BubbleUI/BubbleButton';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';

interface ReserveTransactionsParams {
  property: string;
}

type ReserveTransactionsProps = RouteComponentProps<ReserveTransactionsParams>;

type PaginationParams<T> = {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  archived: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  propertyId?: string;
  hosts?: string | string[];
};

interface TransactionFilters {
  selectedHosts: Array<BubbleTypeaheadValue<SimpleUser>>;
}

function makeDefaultFilters(user: User): TransactionFilters {
  const selectedHosts: Array<BubbleTypeaheadValue<SimpleUser>> = [];
  if (canHostEvents(user)) {
    selectedHosts.push({ key: user._id, item: user, refKey: user._id });
  }
  return { selectedHosts };
}

interface ReserveTransactionsQueryStrings {
  reservation?: string;
}

function getQueryStrings(): ReserveTransactionsQueryStrings {
  const qs = qslib.readQs();

  const reservation =
    hasOwnProperty(qs, 'reservation') && typeof qs.reservation === 'string'
      ? qs.reservation
      : undefined;

  return { reservation };
}

export function makeReservationTableItem<T extends Reservation>(
  res: T,
  usersDict: Record<string, SimpleUser>,
  hourFormat: HourFormat,
  indirectReservationIds: string[],
  serverDate: DateGroup
): ReservationTableItem<T> {
  const ticketsById = mapBy(res.eventSnapshot.reservables.tickets, '_id');
  const summary = summarizeOccurrences(
    [res, ...(res.additionalOccurrences ?? [])],
    ticketsById,
    usersDict,
    { hourFormat, condenseDates: true }
  );

  const isIndirect = indirectReservationIds.includes(res._id);
  const isFlattened = isFlattenedReservation(res);

  const enhancedStatus = getEnhancedReservationStatus(res);

  const item: ReservationTableItem<T> = {
    id: res._id,
    key: `${res._id}_${isIndirect ? 'indirect' : 'direct'}_${
      isFlattened ? `occurrence_${res._occurrenceIndex}` : 'reservation'
    }`,
    event: res.eventSnapshot.title,
    host: summary.hosts,
    rental: summary.rentals,
    date: summary.dates,
    time: summary.timeStrings,
    status: enhancedStatus,
    statusVariant: getEnhancedStatusVariant(res),
    attendance: res.attendance,
    attendanceVariant: (res.attendance === 'no-show'
      ? 'crimson'
      : res.attendance === 'attended'
      ? 'lime'
      : 'smoke') as BubbleBadgeProps['variant'],
    cartCode: res.cartCode || '',
    reservationCode: res.reservationCode,
    name: makeFullName(res.customer.firstName, res.customer.lastName),
    email: res.customer.email,
    phone: res.customer.phone,
    isFlattened,
    isIndirect,
    isReleased: isReservationReleased(res, serverDate.iso),
    isCancelled: res.status === 'cancelled',
    isOccurrence: isFlattened ? !!res._isOccurrenceOf : false,
    totalOccurrences: isFlattened ? res._totalOccurrences : 1,
    sourceReservationId: isFlattened ? res._isOccurrenceOf || res._id : res._id,
    analytics: calculateReservationAnalytics([res]),
    _reservation: res,
    _event: res.eventSnapshot,
  };

  return item;
}

export function makeReservationTableFields(): ReservationTableFields[] {
  return [
    { key: 'status', behavior: BubbleTableFieldBehavior.MinWidth },
    { key: 'reservationCode', label: 'Reservation', behavior: BubbleTableFieldBehavior.NoWrap },
    { key: 'date' },
    { key: 'time' },
    { key: 'host', label: 'Rental / Host' },
    { key: 'name' },
    { key: 'email' },
    { key: 'phone' },
    { key: 'stats', label: '', behavior: BubbleTableFieldBehavior.Condense },
    { key: 'revenue' },
    { key: 'createdAt', behavior: BubbleTableFieldBehavior.NoWrap },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
      render() {
        return (
          <BubbleTooltip
            textRender={() => (
              <div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="edit" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Edit</div>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="trash-alt" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Cancel</div>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="circle-dashed" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Release</div>
                </div>
              </div>
            )}
          >
            <div>
              Actions <FontAwesomeIcon icon="info-circle" />
            </div>
          </BubbleTooltip>
        );
      },
    },
  ];
}

export function makeReservationTableTemplates(actionProps?: Omit<ReservationActionProps, 'data'>) {
  const list: Array<BubbleTableTemplate<ReservationTableItem>> = [
    {
      key: 'status',
      render: ({ statusVariant, status, isOccurrence, isIndirect, isReleased }) => (
        <div className="flex flex-wrap">
          <BubbleBadge className="mt-1 mr-1" variant={statusVariant}>
            {camelToSentenceCase(status)}
          </BubbleBadge>
          {isOccurrence && (
            <BubbleBadge variant="ocean" className="mt-1 mr-1">
              Occurrence
            </BubbleBadge>
          )}
          {isIndirect && (
            <BubbleBadge variant="banana" className="mt-1 mr-1">
              Indirect
            </BubbleBadge>
          )}
          {isReleased && (
            <BubbleBadge variant="ecru" className="mt-1 mr-1">
              Released
            </BubbleBadge>
          )}
        </div>
      ),
    },
    {
      key: 'reservationCode',
      render: ({ reservationCode, cartCode }) => (
        <div>
          <div>{reservationCode}</div>
          {cartCode && <div className="text-xs text-slate">{cartCode}</div>}
        </div>
      ),
    },
    {
      key: 'host',
      render: ({ host, rental }) =>
        (host || rental) && (
          <div>
            {rental && <div>{rental}</div>}
            <div className={`${rental ? 'text-xs text-slate' : ''}`}>{host}</div>
          </div>
        ),
    },
    {
      key: 'name',
    },
    {
      key: 'email',
    },
    {
      key: 'phone',
    },
    {
      key: 'stats',
      render: ({ analytics }) => (
        <div>
          <BubbleBadge
            className="mt-1 mr-1 whitespace-nowrap-i"
            variant={analytics.numOfTickets > 0 ? 'lime' : 'smoke'}
          >
            <FontAwesomeIcon fixedWidth className="mr-1" icon="ticket" />
            <span>{analytics.numOfTickets}</span>
          </BubbleBadge>
          <BubbleBadge
            className="mt-1 mr-1 whitespace-nowrap-i"
            variant={analytics.numOfPackages > 0 ? 'banana' : 'smoke'}
          >
            <FontAwesomeIcon fixedWidth className="mr-1" icon="box-open" />
            <span>{analytics.numOfPackages}</span>
          </BubbleBadge>
          <BubbleBadge
            className="mt-1 mr-1 whitespace-nowrap-i"
            variant={analytics.numOfAddons > 0 ? 'ocean' : 'smoke'}
          >
            <FontAwesomeIcon fixedWidth className="mr-1" icon="puzzle-piece" />
            <span>{analytics.numOfAddons}</span>
          </BubbleBadge>
        </div>
      ),
    },
    {
      key: 'revenue',
      render: data => (
        <span
          className={`${
            data.analytics.totalRevenue === 0
              ? ''
              : data.analytics.totalRevenue > 0
              ? 'font-bold text-lime-darkest'
              : 'font-bold text-crimson'
          }`}
        >
          {formatCentsToUSD(data.analytics.totalRevenue)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      render: item => {
        let element = <></>;

        if (item._reservation.createdAt) {
          const createdAt = getLocaleDateTimeStringLocal(new Date(item._reservation.createdAt));
          const [date, time] = createdAt.split(', ');
          element = (
            <>
              <div className="block whitespace-nowrap">{date}</div>
              <div className="block whitespace-nowrap">{time}</div>
            </>
          );
        }

        return element;
      },
    },
  ];

  if (actionProps) {
    list.push({
      key: 'actions',
      render: data => <ReservationActions {...actionProps} data={data._reservation} />,
    });
  }

  return list;
}

export default function ReserveTransactions(props: ReserveTransactionsProps) {
  const { activeProperty: property } = useActiveProperty();
  const { user } = useUserInfo();
  const { serverDate } = usePublicServerInfo();

  const hourFormat = getHourFormat(property);

  const [loading, setLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [mode, _setMode] = useState<ReservationFormMode | undefined>(undefined);
  const [transactions, setTransactions] = useState<Reservation[]>([]);
  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});
  const [filters, setFilters] = useState<TransactionFilters>(() => makeDefaultFilters(user));
  const [changingStatus, setChangingStatus] = useState(false);

  const queries = readQs();

  const [paginationMetadata, setPaginationMetadata] =
    useState<PaginationMetadata | undefined>(undefined);
  const [paginationParams, setPaginationParams] = useState<PaginationParams<Reservation>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'createdAt',
    sortDirection: SortOrder.DESC,
    startDate: serverDate.iso,
    endDate: '',
    archived: false,
    propertyId: property._id,
    search: typeof queries.search === 'string' ? queries.search : '',
    hosts: canHostEvents(user) ? [user._id] : undefined,
  });

  async function fetchData() {
    setLoading(true);

    try {
      const [users, trans] = await Promise.all([
        getSimpleUsers(property._id),
        paginateReservations({
          page: paginationParams.page,
          perPage: paginationParams.perPage,
          sortBy: paginationParams.sortBy,
          sortDirection: paginationParams.sortDirection,
          propertyId: property._id,
          ...(paginationParams.search && { search: paginationParams.search }),
          ...(paginationParams.archived && { archived: paginationParams.archived }),
          ...(paginationParams.startDate && { startDate: paginationParams.startDate }),
          ...(paginationParams.endDate && { endDate: paginationParams.endDate }),
          ...(paginationParams.hosts && { hosts: paginationParams.hosts }),
        }),
      ]);

      setPaginationMetadata(trans.meta);

      setUsers(users.filter(canHostEvents));
      setTransactions(paginationParams.page > 1 ? [...transactions, ...trans.data] : trans.data);
      refreshSelectedReservation().catch(console.error);
    } finally {
      setLoading(false);
    }
  }

  async function handleExportCSV() {
    setIsBusy(true);

    try {
      let startDate;
      let endDate;
      let includeCancelled = true;
      let multipleRowsPerTransaction = false;
      let includeOtherHosts = false;

      const canSeeAllHosts = canManageAllReservations(user);

      const shouldContinue = await Alert.question({
        htmlContent: (
          <div className="p-3 w-full">
            <div className="grid grid-cols-1 gap-4">
              <BubbleFormGroup label="Transaction Start Date">
                <BubbleInput
                  block
                  type="date"
                  value={startDate}
                  onValue={value => (startDate = value)}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Transaction End Date">
                <BubbleInput
                  block
                  type="date"
                  value={endDate}
                  onValue={value => (endDate = value)}
                />
              </BubbleFormGroup>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="bubble-input"
                  onChange={evt => (includeCancelled = !evt.target.checked)}
                />
                <span className="ml-2 text-sm">Omit Cancelled</span>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="bubble-input"
                  onChange={evt => (multipleRowsPerTransaction = evt.target.checked)}
                />
                <span className="ml-2 text-sm">Multiple Rows per Transaction</span>
              </div>

              {canSeeAllHosts && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    className="bubble-input"
                    onChange={evt => (includeOtherHosts = evt.target.checked)}
                  />
                  <span className="ml-2 text-sm">Ignore Host Filter</span>
                </div>
              )}
            </div>
          </div>
        ),
        title: 'Export CSV',
        yesButton: 'Export',
        noButton: 'Cancel',
      });

      if (!shouldContinue) {
        return;
      }

      const exportType: ReservationExportType = multipleRowsPerTransaction
        ? 'payment_details'
        : 'default';
      const { location } = await exportTransactionsToCsv(
        property._id,
        startDate,
        endDate,
        includeCancelled,
        exportType,
        canSeeAllHosts && includeOtherHosts ? undefined : filters.selectedHosts.map(h => h.refKey)
      );

      return Alert.success({
        htmlContent: (
          <div className="pt-2 pb-4">
            <Anchor target="_blank" href={location}>
              Download CSV
            </Anchor>
          </div>
        ),
      });
    } finally {
      setIsBusy(false);
    }
  }

  useEffect(() => {
    if (property._id !== paginationParams.propertyId) {
      setPaginationParams({ ...paginationParams, page: 1, propertyId: property._id });
    }
  }, [property]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [paginationParams, property._id]);

  function handleOpenReservationPanel(newMode: ReservationFormMode) {
    _setMode(newMode);
    if (newMode.type === 'edit') {
      qslib.writeQs({ reservation: newMode.edit._id });
    }
  }

  function handleCloseReservationPanel() {
    _setMode(undefined);
    qslib.writeQs({});
  }

  function handleSubmitReservation(newReservation: Reservation) {
    fetchData().catch(console.error);
    handleOpenReservationPanel({
      type: 'edit',
      edit: newReservation,
      goToPayments: true,
    });
  }

  async function refreshSelectedReservation() {
    const parsedQs = getQueryStrings();

    const toSelect = parsedQs.reservation;
    if (!toSelect) return;

    const reservation = await getReservation(property._id, toSelect);

    handleOpenReservationPanel({ type: 'edit', edit: reservation });
  }

  const usersDict = mapBy(users, '_id');
  const tableItems: ReservationTableItem[] = transactions.map(res =>
    makeReservationTableItem(res, usersDict, hourFormat, [], serverDate)
  );

  const hostOpts = users.map(u => ({ key: u._id, item: u, refKey: u._id }));

  const tableFields = makeReservationTableFields();

  tableFields.splice(1, 0, {
    key: 'event',
    label: 'Experience',
  });

  const tableTemplates: BubbleTableTemplate<ReservationTableItem>[] = [
    ...makeReservationTableTemplates({
      changingStatus,
      setChangingStatus,
      showDelete,
      setShowDelete,
      fetchData,
      onEdit: res => handleOpenReservationPanel({ type: 'edit', edit: res }),
    }),
  ];

  return (
    <PageWrapper {...props}>
      <Page className="Reservables absolute flex items-stretch">
        <MenuBar {...props} property={property} />
        <div className="SideMenu bg-turquoise-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={property.brand.logo} />
            <PropertyBanner {...props} property={property} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-4 bg-turquoise-darker`}>
            <fieldset disabled={isBusy} className="grid gap-2">
              {canCreateSomeReservations(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  icon="plus"
                  onClick={() => {
                    handleOpenReservationPanel({
                      type: 'create',
                    });
                  }}
                >
                  Add Reservation
                </BubbleButton>
              )}
              <BubbleButton
                variant="slate"
                className="text-left"
                icon="file-export"
                onClick={handleExportCSV}
              >
                Export to CSV
              </BubbleButton>
              <BubbleButton
                variant="white"
                inverse
                className="text-left border border-white"
                icon="repeat"
                onClick={() => fetchData().catch(console.error)}
              >
                Refresh Data
              </BubbleButton>
            </fieldset>
          </div>
          <BubbleAccordion
            variant="turquoise"
            className="flex-grow flex flex-col border-t border-turquoise-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4 py-4">
              {canManageAllReservations(user) && (
                <BubbleFormGroup label="Host" labelClassName="text-white-i" className="px-4">
                  <BubbleTypeahead
                    block
                    variant="turquoise"
                    placeholder="Filter hosts shown..."
                    menuClassName="max-h-80 overflow-y-auto"
                    value={filters.selectedHosts}
                    options={hostOpts}
                    labelExtractor={u => makeFullName(u.firstName, u.lastName) || 'Unknown User'}
                    onAdd={v => {
                      const selectedHosts = [...filters.selectedHosts, v];
                      setFilters({ ...filters, selectedHosts });
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        hosts: selectedHosts.map(h => h.refKey),
                      });
                    }}
                    onRemove={v => {
                      const selectedHosts = filters.selectedHosts.filter(h => h.key !== v.key);
                      setFilters({ ...filters, selectedHosts });
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        hosts:
                          selectedHosts.length > 0 ? selectedHosts.map(h => h.refKey) : undefined,
                      });
                    }}
                  />
                </BubbleFormGroup>
              )}
              <BubbleFormGroup label="View Options" labelClassName="text-white-i" className="px-4">
                <BubbleCheckbox
                  label="Hide past reservations"
                  className="text-white mt-1"
                  variant="turquoise"
                  checked={!!paginationParams.startDate}
                  onValue={() => {
                    setPaginationParams({
                      ...paginationParams,
                      page: 1,
                      startDate: paginationParams.startDate ? undefined : serverDate.iso,
                    });
                  }}
                />
              </BubbleFormGroup>
            </div>
          </BubbleAccordion>
        </div>
        <div className="ReservablesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="ReservablesHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ReserveNavigation active="reserve" />
            </div>
            <div
              className="absolute w-full bg-blue-lightest flex border-blue-lightest -mx-5 px-5"
              style={{
                height: 'calc(40px + 0.75rem)',
                bottom: 'calc(-40px - 0.75rem)',
                borderBottomWidth: '0.75rem',
                zIndex: 10,
              }}
            >
              <ReserveSubNavigation active="reservations" property={property} />
            </div>
          </div>
          <div className="ReservablesBody mx-5 content-minus-logo-minus-bar min-w-900">
            <div style={{ marginTop: 'calc(40px + 0.75rem)' }}>
              <BubbleOverlay loading={loading}>
                <BubbleTable
                  responsive
                  striped
                  stickyHeader={182}
                  onRowClick={subItem =>
                    handleOpenReservationPanel({ type: 'edit', edit: subItem._reservation })
                  }
                  headVariant="turquoise"
                  fields={tableFields}
                  templates={tableTemplates}
                  items={tableItems}
                  showPretitle
                  pretitleClassName="p-0-i"
                  pretitleRender={() => (
                    <div className="px-4 pt-4 pb-1">
                      <BubbleDebounce
                        onTrigger={[
                          (search: string) =>
                            setPaginationParams({ ...paginationParams, page: 1, search }),
                          500,
                        ]}
                      >
                        {({ debounce }) => (
                          <BubbleInput
                            type="text"
                            variant="turquoise"
                            block
                            value={paginationParams.search}
                            inputClassName="outline-none-i"
                            onValue={debounce}
                            placeholder="Search by event, customer name, email, phone, or reservation code..."
                          />
                        )}
                      </BubbleDebounce>
                    </div>
                  )}
                />
                {paginationMetadata && (
                  <BubbleTablePaginationFooter
                    paginationMetadata={paginationMetadata}
                    perPage={paginationParams.perPage}
                    onLoadMoreClick={() =>
                      setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
                    }
                    onPerPageChange={value =>
                      setPaginationParams({ ...paginationParams, page: 1, perPage: value })
                    }
                  />
                )}
              </BubbleOverlay>
            </div>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode && (
          <BubbleDrawer containerClassName="bg-eggshell" onClose={handleCloseReservationPanel}>
            <ReservationPath
              mode={mode}
              users={users}
              onClose={handleCloseReservationPanel}
              onRefreshReservation={handleSubmitReservation}
              onNavigateToReservation={r => handleOpenReservationPanel({ type: 'edit', edit: r })}
            />
          </BubbleDrawer>
        )}
      </Page>
    </PageWrapper>
  );
}

interface ReservationActionProps {
  data: Reservation;
  changingStatus: boolean;
  setChangingStatus: (newVal: boolean) => void;
  showDelete: { [key: string]: boolean };
  setShowDelete: (newVal: { [key: string]: boolean }) => void;
  fetchData: () => Promise<void>;
  onEdit?: (res: Reservation) => void;
  iconSize?: BubbleButtonProps['iconSize'];
}

export function ReservationActions(actionProps: ReservationActionProps) {
  const { serverDate } = usePublicServerInfo();
  const { user } = useUserInfo();

  async function handleReleaseReservation(reservation: Reservation) {
    if (!actionProps) return;

    const isCurrentlyReleased = !!actionProps.data.spotReleased;

    const shouldContinue = await Alert.question({
      htmlContent: (
        <div className="p-3">
          <p className="mb-3">
            {isCurrentlyReleased
              ? "Today and all upcoming dates for this booking will be saved for this guest again. Other guests won't be able to book them."
              : 'Today and all upcoming dates for this booking will be released so other guests can book them.'}
          </p>
          <p>
            {isCurrentlyReleased
              ? 'If someone has already booked any of these dates, the item will be overbooked and a GO user will have to rebook them to a different item.'
              : 'This booking will stay in place — if another guest books these dates, the item will be overbooked and a GO user will have to rebook one of them to a different item.'}
          </p>
        </div>
      ),
      title: isCurrentlyReleased ? 'Put These Dates Back on Hold?' : 'Make These Dates Available?',
      yesButton: isCurrentlyReleased ? 'Yes, Hold Dates' : 'Yes, Open Dates',
      noButton: 'Cancel',
    });

    if (!shouldContinue) {
      return;
    }

    actionProps.setChangingStatus(true);
    try {
      const targetId = isFlattenedReservation(reservation)
        ? reservation._isOccurrenceOf ?? reservation._id
        : reservation._id;
      await releaseSpot({ ...reservation, _id: targetId });
      await actionProps.fetchData();
    } finally {
      actionProps.setChangingStatus(false);
    }
  }

  async function handleToggleReservation(reservation: Reservation) {
    if (!actionProps) return;
    actionProps.setChangingStatus(true);
    try {
      const targetId = isFlattenedReservation(reservation)
        ? reservation._isOccurrenceOf ?? reservation._id
        : reservation._id;
      await toggleStatus({ ...reservation, _id: targetId });
      actionProps.setShowDelete({ ...actionProps.showDelete, [reservation._id]: false });
      await actionProps.fetchData();
    } finally {
      actionProps.setChangingStatus(false);
    }
  }

  const canReleaseSlot = [
    dateToIsoUTC(actionProps.data.date),
    ...(actionProps.data.additionalOccurrences ?? []).map(o => dateToIsoUTC(o.date)),
  ].some(date => date >= serverDate.iso);

  return (
    <BubbleOverlay loading={actionProps.changingStatus} hideSpinner loadingClassName="rounded">
      {actionProps.onEdit && (
        <BubbleButton
          className="p-2-i"
          variant="navy"
          inverse
          onClick={() => actionProps.onEdit?.(actionProps.data)}
          icon="edit"
          iconSize={actionProps.iconSize}
        />
      )}
      {canManageAllReservations(user) && (
        <>
          {canReleaseSlot && (
            <BubbleButton
              className="p-2-i"
              variant="dirt"
              inverse
              onClick={() => handleReleaseReservation(actionProps.data)}
              icon={actionProps.data.spotReleased ? 'circle-dot' : 'circle-dashed'}
              iconSize={actionProps.iconSize}
            />
          )}
          {!actionProps.showDelete[actionProps.data._id] && (
            <BubbleButton
              className="p-2-i"
              variant="crimson"
              inverse
              onClick={() =>
                actionProps.setShowDelete({
                  ...actionProps.showDelete,
                  [actionProps.data._id]: true,
                })
              }
              icon="trash-alt"
              iconSize={actionProps.iconSize}
            />
          )}
          {actionProps.showDelete[actionProps.data._id] && (
            <>
              <BubbleButton
                className="p-2-i"
                variant="crimson"
                inverse
                onClick={() => handleToggleReservation(actionProps.data)}
                icon="trash-check"
                iconSize={actionProps.iconSize}
              />
              <BubbleButton
                className="p-2-i"
                variant="slate"
                inverse
                onClick={() =>
                  actionProps.setShowDelete({
                    ...actionProps.showDelete,
                    [actionProps.data._id]: false,
                  })
                }
                icon="trash-xmark"
                iconSize={actionProps.iconSize}
              />
            </>
          )}
        </>
      )}
    </BubbleOverlay>
  );
}
